import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./components/partner/partner.css";

const API = process.env.REACT_APP_BACKEND_URL;
const BLANK = { entity_type: "", primary_trade: "", other_trade: "", full_name: "", phone: "", email: "", service_zips: "", logistics_capacity: "standard", agree: false };

const Pillars = () => (
  <div className="pw-pillars" data-testid="intake-pillars">
    <div><b>Zero material cost to you</b><span>We procure materials directly. You bring standard trade tools and your craft.</span></div>
    <div><b>Two-stage mobilization</b><span>50% at job assignment (plus courier mileage when you haul), 50% after the final detail clean.</span></div>
    <div><b>48-hour dispatch notice</b><span>Every job is scheduled in advance inside a 06:00–18:00 site window.</span></div>
  </div>
);

const Choice = ({ name, options, value, onChange }) => (
  <div className="pi-choices" role="radiogroup">
    {options.map((o) => (
      <button type="button" key={o.key} className={`pi-choice ${value === o.key ? "on" : ""}`} onClick={() => onChange(o.key)} data-testid={`intake-${name}-${o.key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{o.label}</button>
    ))}
  </div>
);

const Done = ({ r }) => (
  <div className="pw-card pw-card--ok" data-testid="intake-success">
    <h2 className="pw-h2">Thank you. Your profile has been received.</h2>
    <p className="pw-p">Our team will reach out via Executive Welcome Call for profile confirmation prior to job dispatch.{r.courier_eligible ? " Your hauling capacity is noted for paid Material Courier opportunities." : ""}</p>
    <p className="pw-p pw-muted">Want to get ahead? Review the partnership terms, sign the Master Trade Agreement and set up payouts now.</p>
    <a className="pw-btn gw-cta" href={`/partner/${r.partner_id}`} data-testid="intake-continue-onboarding">Continue to secure onboarding</a>
  </div>
);

export default function PartnerIntake() {
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const ref = params.get("ref");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/trade-network/intake/config`).then((r) => r.json()).then(setCfg).catch(() => setErr("The intake page could not load. Please refresh."));
    if (ref) fetch(`${API}/api/trade-network/partner/${ref}`).then((r) => (r.ok ? r.json() : null))
      .then((p) => p && setF((x) => ({ ...x, full_name: p.contact_name || "", email: p.contact_email || "", phone: p.contact_phone || "" }))).catch(() => {});
  }, [ref]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.entity_type || !f.primary_trade) return setErr("Choose your business type and primary specialty.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/trade-network/intake`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...f, ref }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Please check the form and try again.");
      setDone(d);
      setTimeout(() => document.querySelector('[data-testid="intake-success"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <label className="pw-field">{label}<input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`intake-${k}`} {...props} /></label>
  );

  return (
    <div className="pw-page" data-testid="partner-intake-page">
      <header className="pw-head">
        <div className="pw-brand">MOOVIN FORWARD LLC</div>
        <div className="pw-sub">Preferred Partner Network Intake · Fort Worth, TX</div>
      </header>
      <main className="pw-main">
        <h1 className="pw-h1" data-testid="intake-headline">Build Community. Execute Craft. Earn Fair, Fast Payouts.</h1>
        <p className="pw-lead">We are onboarding specialized trade companies, sole proprietors, local independent businesses and 1099 skilled craftsmen for residential make-ready and physical compliance projects across Fort Worth and DFW.</p>
        <Pillars />
        {done ? <Done r={done} /> : !cfg ? <div className="pw-p pw-muted" data-testid="intake-loading">Preparing your intake form…</div> : (
          <form className="pw-card" onSubmit={submit} data-testid="intake-form">
            <h2 className="pw-h2">Business type</h2>
            <Choice name="entity" options={cfg.entity_types.map((x) => ({ key: x, label: x }))} value={f.entity_type} onChange={(v) => set("entity_type", v)} />
            <h2 className="pw-h2">Primary specialty</h2>
            <Choice name="trade" options={cfg.primary_trades.map((x) => ({ key: x, label: x }))} value={f.primary_trade} onChange={(v) => set("primary_trade", v)} />
            {f.primary_trade === "Other" && field("other_trade", "Your specialty", { required: true, placeholder: "e.g. Countertop resurfacing" })}
            <h2 className="pw-h2">Contact information</h2>
            <div className="pw-grid">
              {field("full_name", "Full name / entity name", { required: true, autoComplete: "name" })}
              {field("phone", "Phone number", { required: true, type: "tel", autoComplete: "tel" })}
              {field("email", "Email address", { required: true, type: "email", autoComplete: "email" })}
              {field("service_zips", "Primary DFW service radius / ZIP codes", { required: true, placeholder: "76104, 76110, 76133" })}
            </div>
            <h2 className="pw-h2">Transportation & hauling capacity</h2>
            <Choice name="capacity" options={cfg.logistics_capacity} value={f.logistics_capacity} onChange={(v) => set("logistics_capacity", v)} />
            <h2 className="pw-h2">Standard Agreement</h2>
            <label className="pw-check"><input type="checkbox" checked={f.agree} onChange={(e) => set("agree", e.target.checked)} data-testid="intake-agree" /> {cfg.acknowledgment}</label>
            {err && <div className="pw-err" data-testid="intake-error">{err}</div>}
            <button className="pw-btn" disabled={busy || !f.agree} data-testid="intake-submit-btn">{busy ? "Submitting…" : "Submit partner profile"}</button>
          </form>
        )}
        {err && !cfg && <div className="pw-err" data-testid="intake-load-error">{err}</div>}
      </main>
      <footer className="pw-foot">Creating Jobs. Pushing Love. Building Community.<br />™ &amp; © 2026 Moovin Forward LLC. All Rights Reserved.</footer>
    </div>
  );
}
